import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Plus, Trash2, Download, UserPlus } from 'lucide-react';

const EMPTY_LINE = { nom: '', numero: '', taille: '' };

export default function Personalization() {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [exporting, setExporting] = useState(false);

  // New client dialog
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    client_name: '', client_firstname: '', client_country: '', client_address: '', client_phone: '', client_email: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clients/list`, { withCredentials: true });
      setClients(res.data);
    } catch (e) { console.error(e); }
  };

  const addLine = () => setLines(prev => [...prev, { ...EMPTY_LINE }]);

  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

  const updateLine = (idx, field, value) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const handleCreateClient = async () => {
    if (!newClientForm.client_name.trim()) {
      toast.error('Le nom du client est requis');
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, newClientForm, { withCredentials: true });
      toast.success(`Client "${newClientForm.client_name}" créé`);
      setNewClientOpen(false);
      setSelectedClient(newClientForm.client_name);
      setNewClientForm({ client_name: '', client_firstname: '', client_country: '', client_address: '', client_phone: '', client_email: '' });
      fetchClients();
    } catch (e) {
      toast.error('Erreur lors de la création du client');
    }
  };

  const handleExport = async () => {
    const filledLines = lines.filter(l => l.nom || l.numero || l.taille);
    if (!selectedClient || filledLines.length === 0) {
      toast.error(t('fill_fields'));
      return;
    }
    setExporting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/personalization/export`,
        { client_name: selectedClient, lines: filledLines },
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `personnalisation_${selectedClient.replace(/ /g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('file_downloaded'));
    } catch (e) {
      toast.error('Erreur export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <h1 data-testid="personalization-title" className="text-2xl font-semibold text-slate-900 tracking-tight">
          {t('personalization')}
        </h1>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-medium">{t('client_selection')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">{t('client_name')}</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="client-select" className="border-slate-200">
                    <SelectValue placeholder={t('select_client')} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="new-client-btn" variant="outline" onClick={() => setNewClientOpen(true)} className="border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50">
                <UserPlus size={14} className="mr-1" /> {t('new_client')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-medium">{t('personalization_lines')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 text-xs font-semibold text-slate-600 uppercase px-1">
              <span>{t('name')}</span>
              <span>{t('number')}</span>
              <span>{t('size')}</span>
              <span></span>
            </div>
            {lines.map((line, idx) => (
              <div key={`perso-${idx}-${line.nom || 'empty'}`} className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 items-center">
                <Input
                  data-testid={`perso-nom-${idx}`}
                  value={line.nom}
                  onChange={(e) => updateLine(idx, 'nom', e.target.value)}
                  placeholder="NOM"
                  className="border-slate-200 h-9"
                />
                <Input
                  data-testid={`perso-numero-${idx}`}
                  value={line.numero}
                  onChange={(e) => updateLine(idx, 'numero', e.target.value)}
                  placeholder="00"
                  className="border-slate-200 h-9"
                />
                <Input
                  data-testid={`perso-taille-${idx}`}
                  value={line.taille}
                  onChange={(e) => updateLine(idx, 'taille', e.target.value)}
                  placeholder="M"
                  className="border-slate-200 h-9"
                />
                <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} disabled={lines.length <= 1} className="text-red-500 hover:text-red-700 h-9 w-9 p-0">
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}

            <Button data-testid="add-line-btn" variant="outline" size="sm" onClick={addLine} className="w-full border-dashed">
              <Plus size={14} className="mr-1" /> {t('add_line')}
            </Button>

            <div className="pt-4 border-t border-slate-100">
              <Button data-testid="export-excel-btn" onClick={handleExport} disabled={exporting} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Download size={14} className="mr-2" />
                {exporting ? '...' : t('validate_download')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* New Client Dialog */}
        <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>
          <DialogContent className="max-w-md" data-testid="new-client-dialog">
            <DialogHeader>
              <DialogTitle>{t('new_client')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('last_name')}</Label>
                  <Input data-testid="new-client-name" value={newClientForm.client_name} onChange={(e) => setNewClientForm({ ...newClientForm, client_name: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('first_name')}</Label>
                  <Input data-testid="new-client-firstname" value={newClientForm.client_firstname} onChange={(e) => setNewClientForm({ ...newClientForm, client_firstname: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('country')}</Label>
                  <Input data-testid="new-client-country" value={newClientForm.client_country} onChange={(e) => setNewClientForm({ ...newClientForm, client_country: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('phone')}</Label>
                  <Input data-testid="new-client-phone" value={newClientForm.client_phone} onChange={(e) => setNewClientForm({ ...newClientForm, client_phone: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('address')}</Label>
                <Input data-testid="new-client-address" value={newClientForm.client_address} onChange={(e) => setNewClientForm({ ...newClientForm, client_address: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input data-testid="new-client-email" type="email" value={newClientForm.client_email} onChange={(e) => setNewClientForm({ ...newClientForm, client_email: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button data-testid="new-client-submit" onClick={handleCreateClient} className="flex-1 bg-slate-900 hover:bg-slate-800">{t('save')}</Button>
                <Button variant="outline" onClick={() => setNewClientOpen(false)}>{t('cancel')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
